import { Task } from '@models/task.model';
import { Event } from '@models/event.model';
import { parseDateOnly } from '@utils/formatDate';

/**
 * A precedent that finishes after the dependent task is due to start.
 *
 * The relationship is only meaningful in one direction — a precedent has to be
 * done before the task it precedes can begin — so a precedent ending later than
 * the task's start is always a scheduling contradiction, no matter which of the
 * two the user edited.
 */
export interface PrecedentConflict {
  type: 'task' | 'event';
  id: string;
  name: string;
  endDate: string;
  conflictReason: string;
}

/**
 * Date-only strings must not go through `new Date()` — it reads them as UTC and
 * shifts the day in western timezones, which would flag or hide conflicts around
 * midnight boundaries.
 */
const toTime = (value?: string | Date | null): number | null => {
  if (!value) return null;
  const parsed = parseDateOnly(value as string | Date);
  const time = parsed.getTime();
  return Number.isNaN(time) ? null : time;
};

const asDateString = (value: string | Date): string =>
  typeof value === 'string' ? value : new Date(value).toISOString().split('T')[0];

type FindArgs = {
  /** The task's start date — the proposed one while editing, the saved one otherwise. */
  startTime?: string | Date | null;
  precedentTaskIds?: string[] | null;
  precedentEventIds?: string[] | null;
  tasks: Task[];
  events?: Event[];
};

/**
 * Every precedent that ends after `startTime`.
 *
 * Callers pass the precedent ids explicitly rather than reading them off a task,
 * so the same function serves a form mid-edit (ids the user just picked, not yet
 * saved) and a rendered row (ids already on the record).
 */
export function findPrecedentConflicts({
  startTime,
  precedentTaskIds,
  precedentEventIds,
  tasks,
  events = [],
}: FindArgs): PrecedentConflict[] {
  const startMs = toTime(startTime);
  if (startMs === null) return [];

  const conflicts: PrecedentConflict[] = [];

  for (const precedentId of precedentTaskIds ?? []) {
    const precedent = tasks.find((task) => task.taskId === precedentId);
    if (!precedent?.endTime) continue;

    const endMs = toTime(precedent.endTime);
    if (endMs === null || endMs <= startMs) continue;

    conflicts.push({
      type: 'task',
      id: precedent.taskId as string,
      name: precedent.name,
      endDate: asDateString(precedent.endTime),
      conflictReason:
        'This task must complete before the current task can start.',
    });
  }

  for (const precedentId of precedentEventIds ?? []) {
    const precedent = events.find((event) => event.eventId === precedentId);
    if (!precedent?.eventTime) continue;

    const eventMs = toTime(precedent.eventTime as string | Date);
    if (eventMs === null || eventMs <= startMs) continue;

    conflicts.push({
      type: 'event',
      id: precedent.eventId as string,
      name: precedent.name,
      endDate: asDateString(precedent.eventTime as string | Date),
      conflictReason:
        'This event must occur before the current task can start.',
    });
  }

  return conflicts;
}

/**
 * Convenience wrapper for rendered rows, which already carry their own
 * precedent ids and dates.
 */
export function findConflictsForTask(
  task: Pick<Task, 'startTime' | 'presedentTaskId' | 'presedentEventId'>,
  tasks: Task[],
  events: Event[] = [],
): PrecedentConflict[] {
  return findPrecedentConflicts({
    startTime: task.startTime,
    precedentTaskIds: task.presedentTaskId,
    precedentEventIds: task.presedentEventId,
    tasks,
    events,
  });
}

/** One-line summary for a tooltip or badge label. */
export function describePrecedentConflicts(
  conflicts: PrecedentConflict[],
): string {
  if (conflicts.length === 0) return '';
  if (conflicts.length === 1) {
    return `Precedent task is after current task: "${conflicts[0].name}"`;
  }
  return `${conflicts.length} precedents are after this task: ${conflicts
    .map((conflict) => `"${conflict.name}"`)
    .join(', ')}`;
}
